<template>
  <DialogDrawer
    ref="dialog"
    data-testid="feedback-dialog"
    placement="right"
    modal
    dismissible
    class="h-100"
    @close="close"
  >
    <template #title>{{ translate('Give Feedback to Ekco') }}</template>
    <BootstrapAlert
      v-if="submitted"
      variant="success"
      class="mb-3"
      data-testid="feedback.form.success-message"
    >
      <Icon
        icon="material-symbols:check-circle-outline"
        class="flex-shrink-0 me-3"
        :inline="true"
      />
      <p class="m-0">
        <template v-if="userStore.isEkcoUser">
          {{ translate('Thank you for submitting, you can track progress from') }}
          <router-link to="/tickets" class="alert-link">tickets</router-link>
          {{ translate('page.') }}
        </template>
        <template v-else>
          {{
            translate('Thank you for submitting, we will contact you in case we need more details')
          }}
        </template>
      </p>
    </BootstrapAlert>

    <FeedbackFormPresentational
      v-else
      v-model:type="type"
      v-model:title="title"
      v-model:problem-type="problemType"
      v-model:page-url="pageUrl"
      v-model:summary="summary"
      :problem-type-options="problemTypeOptions"
      :error-messages="errorMessages"
      :loading="loading"
      :server-error="serverError"
      @submit="submitFeedback"
    />

    <template #buttons>
      <BootstrapButton
        v-if="submitted"
        name="close-button"
        variant="primary"
        data-testid="feedback.form.button.close"
        @click="close"
      >
        {{ translate('Close') }}
      </BootstrapButton>
      <template v-else>
        <BootstrapButton
          name="cancel-button"
          variant="secondary"
          data-testid="feedback.form.button.cancel"
          @click="close"
        >
          {{ translate('Cancel') }}
        </BootstrapButton>
        <BootstrapButton
          name="submit-button"
          type="submit"
          form="feedback-form"
          :disabled="loading"
          :loading="loading"
          data-testid="feedback.form.button.send"
        >
          {{ translate('Send to Ekco') }}
        </BootstrapButton>
      </template>
    </template>
  </DialogDrawer>
</template>
<script setup>
import axios from 'axios';
import {useLocalisation} from '@/composables/useLocalisation';
import FeedbackFormPresentational from './FeedbackFormPresentational.vue';

const emits = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
});

const routeLocation = useRoute();
const userStore = useUserStore();
const customersStore = useCustomersStore();
const {translate} = useLocalisation();

const dialog = ref();
const type = ref('feedback');
const title = ref('');
const problemType = ref('');
const pageUrl = ref('');
const summary = ref('');
const loading = ref(false);
const submitted = ref(false);

const errorMessages = ref(new Map());
const serverError = ref(false);

const problemTypeOptions = [
  'Report data is incorrect/does not load',
  'Content is incorrect',
  'Do not have access to specific page',
  'Other',
];

const route = computed(() => routeLocation.path);

watch(type, (newType) => {
  if (newType === 'problem') {
    if (!pageUrl.value) {
      pageUrl.value = window.location.href;
    }
  } else if (newType === 'feedback') {
    problemType.value = '';
    pageUrl.value = '';
  }
});

const resetForm = () => {
  type.value = 'feedback';
  title.value = '';
  problemType.value = '';
  pageUrl.value = '';
  summary.value = '';
  submitted.value = false;
  loading.value = false;
  serverError.value = false;
  errorMessages.value = new Map();
};

const submitFeedback = async (form) => {
  const key = userStore.user.USER_FEEDBACK_API_KEY;
  /**
   * CORS error, Azure blocks responses to localhost.
   * However, the request does go through to the function.
   */
  try {
    loading.value = true;
    serverError.value = false;
    errorMessages.value = new Map();

    if (form.value) {
      form.value.classList.remove('was-validated');
    }

    const payload = {
      id: crypto.randomUUID(),
      title: title.value,
      submitted_by: userStore.user.email,
      user_name: userStore.user.first_name + ' ' + userStore.user.last_name,
      user_is_staff: userStore.isEkcoUser,
      route: route.value,
      type: type.value,
      ...(problemType.value && {problem_type: problemType.value}),
      ...(pageUrl.value && {page_url: pageUrl.value}),
      feedback: summary.value,
      customer_name: customersStore.selectedCustomer.customer.name,
      customer_debtor_number: customersStore.selectedCustomer.customer.debtor_number,
      fresh_requester_id: userStore.user?.fresh_requester_id,
      region: customersStore.selectedCustomer.customer.region,
      eventTime: new Date().toUTCString(),
      profile: userStore.user,
      customer: customersStore.selectedCustomer,
    };

    await axios.post(
      `https://ekco-receive-feedback.azurewebsites.net/api/user_feedback?code=${key}`,
      JSON.stringify(payload),
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      },
    );
    submitted.value = true;
  } catch (e) {
    serverError.value = true;
    errorMessages.value.set(
      'submit',
      e?.message ||
        translate(
          'An unexpected error occurred while submitting your feedback. Please try again later.',
        ),
    );
    if (form.value) {
      form.value.classList.remove('was-validated');
    }
  } finally {
    loading.value = false;
  }
};

const close = () => {
  dialog.value?.close();
  model.value = false;
  resetForm();
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      dialog.value?.openModal();
    } else {
      dialog.value?.close();
    }
  },
);
</script>
